import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckCircle2, CreditCard, Shield, Truck } from "lucide-react";
const summaryItems = [{
  icon: Shield,
  text: "Privacy First Design"
}, {
  icon: Truck,
  text: "Seamless Integration"
}, {
  icon: CheckCircle2,
  text: "Aesthetic Harmony"
}, {
  icon: CreditCard,
  text: "Sustainable Tech"
}];
const policies = [{
  id: "privacy",
  title: "Simplicity",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/70">
        <p>
          We believe that technology should disappear into the background. Our philosophy centers on reducing complexity to its absolute minimum, ensuring that your digital environment remains a place of calm rather than a source of stress.
        </p>
        <p>
          Every interface we design and every automation we build follows the principle of "less but better." By removing the non-essential, we create space for what truly matters in your daily life.
        </p>
        <p>
          Explore our approach to minimalist living in our{" "}
          <Link to="/MozaMethodPage" className="text-primary hover:underline font-medium">
            methodology guide
          </Link>.
        </p>
      </div>
}, {
  id: "refund",
  title: "Privacy",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/70">
        <p>
          Your home is your sanctuary, and your data should stay within its walls. We prioritize local-first processing and end-to-end encryption in every smart home recipe we share.
        </p>
        <p>
          We advocate for devices that respect user autonomy. Our reviews and recommendations are strictly filtered through a privacy-centric lens, ensuring no third-party cloud dependencies unless absolutely necessary.
        </p>
        <p>
          To learn more about securing your network, visit our{" "}
          <Link to="/SmartHomeTipsAndTricksPage" className="text-primary hover:underline font-medium">
            Smart Home Tips
          </Link>{" "}
          section.
        </p>
      </div>
}, {
  id: "shipping",
  title: "Aesthetics",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/70">
        <p>
          Functionality should never come at the cost of beauty. We select hardware that complements your interior design, treating smart devices as modern artifacts rather than mere gadgets.
        </p>
        <p>
          From the tactile feel of a switch to the subtle glow of an LED indicator, every detail is considered. We help you curate a space that feels cohesive, warm, and intentionally designed.
        </p>
        <p>
          View our curated gallery of aesthetic setups in the{" "}
          <Link to="/DeviceReviewsPage" className="text-primary hover:underline font-medium">
            Device Reviews
          </Link>.
        </p>
      </div>
}, {
  id: "terms",
  title: "The Moza Way",
  content: <div className="max-w-2xl space-y-4 leading-relaxed text-foreground/70">
        <p>
          The Moza Way is a commitment to intentional living through technology. It is a framework for those who seek the benefits of automation without the clutter of the modern digital world.
        </p>
        <p>
          By following our recipes and guides, you are joining a community dedicated to Digital Zen. We provide the tools; you provide the vision for your ideal living space.
        </p>
        <p>
          All our content is crafted with care to ensure you can implement these changes with confidence and ease.
        </p>
      </div>
}];
export default function TheTransparencyReport() {
  return <section data-section-id="224310" id="our-philosophy" className="w-full bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="mb-12">
          <h2 className="font-default text-4xl md:text-5xl font-bold tracking-tighter text-foreground">
            The Moza Way
          </h2>
          <p className="mt-4 text-xl text-foreground/60 max-w-2xl font-serif italic">
            A philosophy of Digital Zen. We believe in complete harmony between your lifestyle and the technology that supports it.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-10 lg:gap-16 items-start">
          {}
          <aside className="border border-border bg-accent/30 p-6 lg:sticky lg:top-24 rounded-2xl shadow-sm">
            <h3 className="font-default text-lg font-semibold text-foreground mb-6">
              Core Pillars
            </h3>
            <ul className="space-y-5">
              {summaryItems.map((item, i) => {
              const Icon = item.icon;
              return <li key={i} data-index={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-secondary flex items-center justify-center shrink-0 mt-0.5">
                      <Icon className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground/80 font-default">
                      {item.text}
                    </span>
                  </li>;
            })}
            </ul>
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-4 font-serif">
                Ready to transform your space? Our guides are here to help.
              </p>
              <Link to="/GetInTouchPage" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-all bg-primary text-primary-foreground shadow-md hover:bg-primary/90 h-10 px-6 py-2 w-full">
                Get in Touch
              </Link>
            </div>
          </aside>

          {}
          <div className="w-full">
            <Accordion type="single" collapsible className="w-full" defaultValue="privacy">
              {policies.map((policy, i) => <AccordionItem key={policy.id} value={policy.id} data-index={i} className="border-border">
                  <AccordionTrigger className="font-default text-xl font-semibold hover:no-underline hover:text-secondary-foreground transition-colors py-6">
                    {policy.title}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 font-serif">
                    {policy.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}